import React from 'react'
import { Box, Button, Heading, Image, Spinner, Stack, Text } from '@chakra-ui/react'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'
import { getMyActivityEvent, logger, type DonationEvent } from '@kuow/core'
import { useAuth } from '@kuow/shared-ui'
import { ReceiptBody } from '../components/donation-history/ReceiptBody'
import kuowLockup from '../assets/kuow-npr-lockup.svg'

type State =
  | { status: 'loading' }
  | { status: 'loaded'; event: DonationEvent }
  | { status: 'error' }

export const MemberReceiptPrintPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const { user } = useAuth()
  const [state, setState] = React.useState<State>({ status: 'loading' })

  const memberName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || 'KUOW Member'

  React.useEffect(() => {
    if (!eventId) { setState({ status: 'error' }); return }
    const controller = new AbortController()
    setState({ status: 'loading' })
    void (async () => {
      try {
        const event = await getMyActivityEvent(eventId, { signal: controller.signal })
        setState({ status: 'loaded', event })
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return
        logger.error('Failed to load receipt for print page', { eventId, error })
        setState({ status: 'error' })
      }
    })()
    return () => controller.abort()
  }, [eventId])

  return (
    <Box maxW="lg" mx="auto" px={4} py={{ base: 6, md: 10 }}>
      <Helmet><title>Payment Receipt | KUOW</title></Helmet>
      <Stack align="center" gap={3} mb={8}>
        <Image src={String(kuowLockup)} alt="KUOW" h={{ base: 7, md: 8 }} />
        <Heading as="h1" size="md" fontWeight="bold">Payment Receipt</Heading>
      </Stack>
      {state.status === 'loading' ? (
        <Stack align="center" py={10}><Spinner /></Stack>
      ) : state.status === 'error' ? (
        <Text fontSize="sm" color="fg.muted">We couldn't find that receipt.</Text>
      ) : (
        <Stack gap={6}>
          <ReceiptBody event={state.event} memberName={memberName} />
          <Box css={{ '@media print': { display: 'none' } }}>
            <Button variant="outline" size="sm" onClick={() => window.print()}>Print</Button>
          </Box>
        </Stack>
      )}
    </Box>
  )
}
